/**
 * DEFCAT P6 — publish-diff modal. Fetches GET .../diff for the draft (against
 * the current published version), shows added / removed / changed entries +
 * validity delta, then POSTs publish on confirm. A 422 PublishValidationError
 * renders its issues inline (publish stays available after fixing).
 */
import { useState, type ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type { PublishValidationError } from "@aeriosiq/types";
import {
  useCatalogueDiffQuery,
  usePublishVersionMutation,
} from "./taxonomyQueries.js";
import { locText } from "./locText.js";

export interface PublishDiffDialogProps {
  open: boolean;
  catalogueId: string;
  versionId: string;
  onClose: () => void;
  onPublished: () => void;
}

interface AxiosLikeError {
  status?: number;
  cause?: { response?: { status?: number; data?: unknown } };
}

function extractPublishIssues(
  err: unknown,
): PublishValidationError["issues"] | null {
  const e = err as AxiosLikeError;
  const status = e.status ?? e.cause?.response?.status;
  if (status !== 422) return null;
  const data = e.cause?.response?.data as PublishValidationError | undefined;
  return data?.issues ?? null;
}

export function PublishDiffDialog({
  open,
  catalogueId,
  versionId,
  onClose,
  onPublished,
}: PublishDiffDialogProps): ReactElement {
  const [notes, setNotes] = useState("");
  const diff = useCatalogueDiffQuery(catalogueId, versionId, null, open);
  const publish = usePublishVersionMutation(catalogueId, versionId);

  const issues = publish.isError ? extractPublishIssues(publish.error) : null;

  function handlePublish(): void {
    publish.mutate(
      { notes: notes.trim().length > 0 ? notes.trim() : null },
      {
        onSuccess: () => {
          setNotes("");
          onPublished();
        },
      },
    );
  }

  const d = diff.data;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Publish draft — review changes</DialogTitle>
      <DialogContent dividers>
        {diff.isLoading ? (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress aria-label="Loading diff" />
          </Stack>
        ) : diff.isError ? (
          <Alert severity="error">Could not load the diff preview.</Alert>
        ) : d ? (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1}>
              <Chip
                color="success"
                label={`${String(d.addedEntries.length)} added`}
              />
              <Chip
                color="error"
                label={`${String(d.removedEntryIds.length)} removed`}
              />
              <Chip
                color="warning"
                label={`${String(d.changedEntries.length)} changed`}
              />
              <Chip
                label={`Dependencies +${String(d.addedValidity.length)} / −${String(
                  d.removedValidity.length,
                )}`}
              />
            </Stack>

            {d.addedEntries.length > 0 ? (
              <Box>
                <Typography variant="subtitle2">Added entries</Typography>
                <List dense>
                  {d.addedEntries.map((e) => (
                    <ListItem key={e.entryId} disableGutters>
                      <ListItemText
                        primary={locText(e.name)}
                        secondary={e.axis ?? undefined}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            ) : null}

            {d.changedEntries.length > 0 ? (
              <Box>
                <Typography variant="subtitle2">Changed entries</Typography>
                <List dense>
                  {d.changedEntries.map((c) => (
                    <ListItem key={c.entryId} disableGutters>
                      <ListItemText
                        primary={`${locText(c.before.name)} → ${locText(
                          c.after.name,
                        )}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            ) : null}

            <TextField
              label="Publish note (optional)"
              value={notes}
              onChange={(e) => {
                setNotes(e.target.value);
              }}
              fullWidth
              multiline
              minRows={2}
            />
          </Stack>
        ) : null}

        {issues ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            <Typography variant="subtitle2">
              Publish blocked — fix these and retry:
            </Typography>
            <List dense>
              {issues.map((iss, i) => (
                <ListItem key={`${iss.code}-${String(i)}`} disableGutters>
                  <ListItemText primary={iss.message} secondary={iss.code} />
                </ListItem>
              ))}
            </List>
          </Alert>
        ) : publish.isError ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            Publish failed. The draft may already be published or you lack
            permission.
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handlePublish}
          disabled={publish.isPending || diff.isLoading}
        >
          Publish
        </Button>
      </DialogActions>
    </Dialog>
  );
}
